// Data glue for the engineer's site and lift history (Session AEK, Director decision D33; since
// Session ASB, D-SEC-6, only the sites where the engineer holds OPEN work): ties the IndexedDB
// adapter (db.ts), the pure cache policy (historyCache.ts) and the API client together. The React
// layer calls these; it never touches IndexedDB or fetch directly. Mirrors jobsStore.ts /
// messagesStore.ts.
//
// Read-only throughout. Nothing here writes to the platform, so there is no outbox and no queue:
// the worst a failure costs is a screen that says it is showing saved information.
import { api } from "../api";
import type {
  EngineerSiteListItem,
  EngineerSiteHistory,
  EngineerLiftHistory,
  EngineerJobSheetDetail,
} from "../api";
import { historyCacheStore as idbHistoryCacheStore } from "./db";
import { wipeGeneration } from "./wipeEpoch";
import {
  readThroughCache,
  pruneHistoryCache,
  siteListCacheKey,
  siteHistoryCacheKey,
  liftHistoryCacheKey,
  jobSheetCacheKey,
  myVisitsCacheKey,
  SITE_LIST_TTL_MS,
  HISTORY_TTL_MS,
  JOB_SHEET_TTL_MS,
} from "./historyCache";
import type { CacheReadResult, PrunableHistoryCacheStore } from "./historyCache";

// Session ASB review (I6-6): the store every read below writes through, taken when the read STARTS. Its putEntry does
// nothing once this browser's offline copies have been wiped since then (a sign-out while the request was in flight),
// so a late answer never lands back on the device (wipeEpoch.ts). Reading passes straight through, and so do deleting
// and clearing, which can only ever remove data.
function historyCacheStoreForThisRead(started: number = wipeGeneration()): PrunableHistoryCacheStore {
  return {
    getEntry: (key) => idbHistoryCacheStore.getEntry(key),
    putEntry: async (entry) => {
      if (wipeGeneration() !== started) return;
      await idbHistoryCacheStore.putEntry(entry);
    },
    deleteEntry: (key) => idbHistoryCacheStore.deleteEntry(key),
    listEntries: () => idbHistoryCacheStore.listEntries(),
    clear: () => idbHistoryCacheStore.clear(),
  };
}

// Session ASB (D-SEC-6): after a site list that came from the SERVER (never one served from the cache),
// drop every cached site page, lift page and job sheet for a site that is not on it. Best-effort: a
// failed prune leaves the entries to the 404 purge (readThroughCache) and to sign-out, and must never
// cost the engineer the list they just loaded.
async function pruneToSiteList(result: CacheReadResult<EngineerSiteListItem[]>): Promise<void> {
  if (result.fromCache) return;
  try {
    await pruneHistoryCache(idbHistoryCacheStore, new Set(result.data.map((site) => site.id)));
  } catch {
    // Best-effort, as above.
  }
}

// The site browse list. Kept for a day between syncs, so the search screen is instant and works with
// no signal; syncSiteList (below) re-fetches it on every PWA sync, so in practice it is as current as
// the job list.
export async function loadSiteList(): Promise<CacheReadResult<EngineerSiteListItem[]>> {
  const started = wipeGeneration();
  const result = await readThroughCache(historyCacheStoreForThisRead(started), siteListCacheKey(), SITE_LIST_TTL_MS, () => api.engineer.sites());
  // A list that arrived after a wipe (I6-6) is nobody's list any more: it prunes nothing.
  if (wipeGeneration() === started) await pruneToSiteList(result);
  return result;
}

// Session ASB (D-SEC-6): re-fetch the site list from the server whatever the cache holds, and prune
// every cached page for a site that has left it. Called on every PWA sync (EngineerDataContext's
// refresh: app opened, foregrounded, back online), beside the job-list refresh, so a site drops off
// the device on the next successful sync after its work is finished, not a day later. Offline, or on
// any failure, the cached list and pages stand exactly as before (the TTL of 0 only forces the fetch;
// the stale fallback is readThroughCache's own). A refusal that takes access away (a 403, or a 401 whose
// reason is signed_out) clears the cache, as on every read; a session that merely ran out leaves it
// (readThroughCache, isRevocation). This is typically the FIRST history read after a session lapses, which
// is where that distinction matters most.
export async function syncSiteList(): Promise<void> {
  const started = wipeGeneration();
  const result = await readThroughCache(historyCacheStoreForThisRead(started), siteListCacheKey(), 0, () => api.engineer.sites({ fresh: true }));
  if (wipeGeneration() === started) await pruneToSiteList(result);
}

// One page of a site's history. Each page is cached under its own key, so paging forward never
// overwrites the page before it and going back is instant.
export function loadSiteHistory(siteId: string, page = 1): Promise<CacheReadResult<EngineerSiteHistory>> {
  return readThroughCache(historyCacheStoreForThisRead(), siteHistoryCacheKey(siteId, page), HISTORY_TTL_MS, () =>
    api.engineer.siteHistory(siteId, page),
  );
}

// One page of a lift's history, including its open defects.
export function loadLiftHistory(assetId: string, page = 1): Promise<CacheReadResult<EngineerLiftHistory>> {
  return readThroughCache(historyCacheStoreForThisRead(), liftHistoryCacheKey(assetId, page), HISTORY_TTL_MS, () =>
    api.engineer.liftHistory(assetId, page),
  );
}

// One completed job sheet in full (Session AEM). Cached for a day: the record is finished, so a
// visit read once on site is readable again in a basement with no signal. Past its day the saved copy
// is re-fetched first, and served, marked stale, when the server has not answered within
// MY_VISITS_TIMEOUT_MS (Session ASB, fix round 4): the record My past visits opens, for a site that has
// left the site list, is usually more than a day old, and one bar of signal must not hold it behind
// "Loading...". With nothing saved the read waits for the answer, however slow (readWithSavedCopyTimeout).
// api.engineer.jobSheet takes no signal, so a request that times out is not aborted: its late answer is
// ignored, as the read has already settled, and is never written back.
export function loadJobSheet(jobId: string): Promise<CacheReadResult<EngineerJobSheetDetail>> {
  return readWithSavedCopyTimeout(jobSheetCacheKey(jobId), JOB_SHEET_TTL_MS, () => api.engineer.jobSheet(jobId));
}

export type MyVisitsPage = Awaited<ReturnType<typeof api.engineer.myVisits>>;

// How long My past visits, and the record it opens, wait for the server before showing the copy saved on the phone
// instead (Session ASB, the review of fix round 2; the record since fix round 4). With no connection at all a request
// fails at once; with one bar of signal in a plant room it can neither succeed nor fail for tens of seconds, and a
// list or record that is ON the phone would sit behind "Loading..." for all of that. Only a read that HAS a saved copy
// gives up (readWithSavedCopyTimeout, below).
export const MY_VISITS_TIMEOUT_MS = 8_000;

// A read that gives up after `ms`: the signal it hands the request is aborted (so a request that listens to it, as My
// past visits' does, lets the stalled connection go) and the read fails as a request with no answer does, with no
// status, so readThroughCache serves the saved copy marked stale. Whatever arrives after that is ignored: the read has
// already settled, so nothing late is written back. A request that throws before it starts rejects at once and leaves
// no timer behind.
export class RequestTimedOutError extends Error {
  constructor(ms: number) {
    super(`No answer from the server within ${ms} ms`);
    this.name = "RequestTimedOutError";
  }
}

export function withTimeout<T>(request: (signal: AbortSignal) => Promise<T>, ms: number): Promise<T> {
  const controller = new AbortController();
  return new Promise<T>((resolve, reject) => {
    const pending = request(controller.signal);
    const timer = setTimeout(() => {
      controller.abort();
      reject(new RequestTimedOutError(ms));
    }, ms);
    pending.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

// Time out ONLY onto a saved copy (Session ASB, fix round 4). The timeout exists so a stalled request falls back to
// the copy on the phone. With nothing saved there is nothing to fall back to, and giving up would only turn a slow
// answer, which used to arrive, into a failure that repeats on every open on that link. So the saved entry is looked
// up first: when there is one the request is raced against MY_VISITS_TIMEOUT_MS and handed the timeout's signal;
// when there is none it is left to answer, however long that takes, and is handed no signal at all. A saved copy
// that goes between this look and readThroughCache's own (a sign-out's wipe) leaves a timed-out read with nothing to
// serve, so it fails as any unanswered read does.
async function readWithSavedCopyTimeout<T>(
  key: string,
  ttlMs: number,
  request: (signal?: AbortSignal) => Promise<T>,
): Promise<CacheReadResult<T>> {
  const store = historyCacheStoreForThisRead();
  let hasSaved = false;
  try {
    hasSaved = (await store.getEntry(key)) !== undefined;
  } catch {
    // A broken cache never blocks the read; with no copy it can serve, the read waits for the answer.
  }
  return readThroughCache(store, key, ttlMs, () => (hasSaved ? withTimeout(request, MY_VISITS_TIMEOUT_MS) : request()));
}

// One page of the engineer's OWN completed visits, newest first: "My past visits" (Session ASB fix
// review). Live whenever there is signal: the TTL of 0 always asks the server, as the site-list sync does,
// so a visit the office approved a minute ago is on the list. Each answer is saved, so with no signal, or, when
// the page is saved, no answer within MY_VISITS_TIMEOUT_MS, the page last seen is served instead, marked stale,
// and still links to the engineer's own records, which the prune keeps whatever their site. A page never saved
// waits for the answer, however slow (fix round 4). Written through the wipe-epoch store like every other read here.
export function loadMyVisits(page = 1): Promise<CacheReadResult<MyVisitsPage>> {
  return readWithSavedCopyTimeout(myVisitsCacheKey(page), 0, (signal) =>
    signal ? api.engineer.myVisits(page, { signal }) : api.engineer.myVisits(page),
  );
}

// Drop every cached page. Called whenever the auth layer wipes the offline copies (offlineCache.ts,
// clearOfflineApiCache): a sign-out the person chose, a session the server took away, and a sign-in by
// somebody else. The history is company data, and a shared device must not keep it readable once nobody is
// signed in. (Session ASB: pages for a site that has left the engineer's scope are dropped sooner, by the
// prune above and the 404 purge. The engineer's own My past visits pages and records are kept by the prune;
// they go here, or when a read is refused in a way that takes access away, a 403 or a 401 whose reason is not
// one of the ran-out reasons (RAN_OUT_END_REASONS in sessionEvents.ts), which clears the whole store:
// readThroughCache and isRevocation. A 401 for a session that merely ran out, idle_timeout, session_expired or
// no_session (or a 401 with no reason at all), clears nothing here: whether the
// copies are kept is the auth layer's decision, and for an engineer it keeps them.)
export function clearHistoryCache(): Promise<void> {
  return idbHistoryCacheStore.clear();
}

// Filter the site list as the engineer types. Matches the site name, the address or the postcode,
// case- and space-insensitively on the postcode so "e177lg" finds "E17 7LG". Pure, so the search
// behaviour is tested without a browser.
export function filterSites(sites: EngineerSiteListItem[], query: string): EngineerSiteListItem[] {
  const q = query.trim().toLowerCase();
  if (!q) return sites;
  const squashed = q.replace(/\s+/g, "");
  return sites.filter((site) => {
    if (site.name.toLowerCase().includes(q)) return true;
    if (site.address.toLowerCase().includes(q)) return true;
    const postcode = (site.postcode ?? "").toLowerCase().replace(/\s+/g, "");
    if (postcode && squashed && postcode.includes(squashed)) return true;
    // An engineer often knows the lift, not the site: let an ELE number find its site too.
    return site.eleNumbers.some((n) => n.toLowerCase().includes(q));
  });
}

// Split an ALREADY-FILTERED site list into the two sections the browse screen shows (Session AEM,
// Director decision). Order within each section is preserved, so both stay in the server's
// name order.
//
// The split is applied AFTER the search filter, never before: the search has to reach an archived
// site (that is the whole reason they are kept), so filtering first and splitting second is what
// lets the screen say "this many archived sites match" and open the section on a hit.
export interface SplitSites {
  active: EngineerSiteListItem[];
  archived: EngineerSiteListItem[];
}

export function splitSitesByArchived(sites: EngineerSiteListItem[]): SplitSites {
  const active: EngineerSiteListItem[] = [];
  const archived: EngineerSiteListItem[] = [];
  for (const site of sites) (site.archived ? archived : active).push(site);
  return { active, archived };
}
